<template>
  <div
    ref="parallaxContainerRef"
    v-editable="blok"
    class="TextImageVideoBlock"
    :class="[desktopWidth('full'), blockPaddingTop('md'), blockPaddingBottom('md')]"
  >
    <div
      :key="'videoKey' + videoKey"
      class="layout-container grid grid-cols-6 desk:grid-cols-12 gap-32 desk:gap-16"
    >
      <div
        ref="imageContainer"
        class="grid grid-cols-subgrid gap-8 desk:gap-16"
        :class="{
          'col-span-6': blok.design !== 'imageOverlay',
          'col-span-6 desk:col-span-7': blok.design === 'imageOverlay',
          'desk:order-2': blok.leftAlignedContent,
        }"
      >
        <div
          v-if="blok.design === 'colorBackground'"
          class="col-start-1 col-span-5 row-start-1 row-end-1"
          :class="{
            [blok.imageAspectRatio]: blok.imageAspectRatio,
            'col-start-2 col-span-5': blok.leftAlignedContent,
            'bg-bg-subtle': blok.backgroundColor === 'subtle',
            'bg-bg-secondary': blok.backgroundColor === 'secondary',
            'bg-bg-accenture': blok.backgroundColor === 'accenture',
          }"
        />
        <div
          :class="{
            [blok.imageAspectRatio]: blok.imageAspectRatio,
            'col-start-1 -col-end-1': blok.design === 'default',
            'col-start-2 col-span-5 row-start-1 row-end-1 py-40 desk:py-64': blok.design === 'colorBackground' && !blok.leftAlignedContent,
            'col-start-1 col-span-5 row-start-1 row-end-1 py-40 desk:py-64': blok.design === 'colorBackground' && blok.leftAlignedContent,
            'col-start-1 -col-end-2 row-start-1 row-end-1': blok.design === 'imageOverlay' && !blok.leftAlignedContent,
            'col-start-2 -col-end-1 row-start-1 row-end-1': blok.design === 'imageOverlay' && blok.leftAlignedContent,
            'col-span-4': ['imageGrid', 'imagesGrid'].includes(blok.design) && !blok.leftAlignedContent,
            'col-span-4 order-2': ['imageGrid', 'imagesGrid'].includes(blok.design) && blok.leftAlignedContent,
          }"
        >
          <div
            v-if="checkIfVideoFile(blok.imageOrBackgroundVideo.filename) && !hideVideo"
            class="overflow-hidden w-full h-full"
          >
            <video
              :style="blok.parallaxEffect ? { transform: `translateY(${offsetLarge}px) scale(1.4)`, transformOrigin: 'bottom center' } : {}"
              width="w-full"
              height="h-full"
              class="w-full h-full object-cover transition-all duration-[50ms]"
              loop
              muted
              autoplay
              playsinline
            >
              <source :src="blok.imageOrBackgroundVideo.filename + `?videoKey=${videoKey}`" type="video/mp4">
            </video>
          </div>
          <div
            v-else-if="blok.imageOrBackgroundVideo && blok.imageOrBackgroundVideo.filename && !hideVideo"
            class="overflow-hidden w-full h-full"
          >
            <nuxt-img
              :style="blok.parallaxEffect ? { transform: `translateY(${reverseOffset}px) scale(1.2)`, transformOrigin: 'top center' } : {}"
              preset="standard"
              loading="lazy"
              sizes="sm:100vw desk:1260px"
              class="h-full w-full object-cover transition-all duration-[50ms]"
              :class="blok.focalPoint"
              :src="blok.imageOrBackgroundVideo.filename"
              :alt="blok.imageOrBackgroundVideo.alt"
            />
          </div>
          <div
            v-if="blok.imageOrBackgroundVideo.name && blok.design !== 'colorBackground'"
            class="mt-8 type-tagline text-text-subtle"
          >
            {{ blok.imageOrBackgroundVideo.name }}
          </div>
        </div>
        <div
          v-if="['imageOverlay', 'imageGrid', 'imagesGrid'].includes(blok.design)"
          :style="blok.parallaxEffect ? { transform: `translateY(${offset}px)` } : {}"
          class="mt-32 desk:mt-64"
          :class="{
            [blok.imageAspectRatio]: blok.imageAspectRatio,
            'col-start-5 desk:col-start-6 col-span-2 row-start-1 row-end-1': blok.design === 'imageOverlay' && !blok.leftAlignedContent,
            'col-start-1 col-span-2 row-start-1 row-end-1': blok.design === 'imageOverlay' && blok.leftAlignedContent,
            'col-span-2': blok.design === 'imageGrid'|| 'imagesGrid',
          }"
        >
          <nuxt-img
            v-if="blok.additionalImage"
            preset="standard"
            loading="lazy"
            sizes="sm:100vw desk:1260px"
            class="h-full w-full object-cover"
            :class="blok.secondFocalPoint"
            :src="blok.additionalImage.filename"
            :alt="blok.additionalImage.alt"
          />

          <div
            v-if="blok.additionalImage.name"
            class="mt-8 type-tagline text-text-subtle"
          >
            {{ blok.additionalImage.name }}
          </div>
          <nuxt-img
            v-if="blok.design === 'imagesGrid'"
            preset="standard"
            loading="lazy"
            sizes="sm:100vw desk:1260px"
            class="h-full w-full object-cover mt-16"
            :class="blok.thirdFocalPoint"
            :src="blok.thirdImage.filename"
            :alt="blok.thirdImage.alt"
          />
          <div
            v-if="blok.thirdImage.name"
            class="mt-8 type-tagline text-text-subtle"
          >
            {{ blok.thirdImage.name }}
          </div>
        </div>
      </div>
      <div
        class="grid grid-cols-subgrid"
        :class="{
          'col-span-6': blok.design !== 'imageOverlay',
          'col-span-6 desk:col-span-5': blok.design === 'imageOverlay',
        }"
      >
        <div
          class="col-start-1 -col-end-1 desk:col-start-2 flex items-center"
          :class="{
            'desk:-col-end-2': blok.design !== 'imageOverlay',
            'desk:col-start-1 desk:-col-end-1': blok.design === 'imageOverlay',
          }"
        >
          <div
            class="flex flex-col gap-12"
            :class="{
              'items-center text-center': !blok.textAlign || blok.textAlign === 'center',
              'items-start': blok.textAlign === 'left',
              'items-end': blok.textAlign === 'right',
            }"
          >
            <div
              class="type-tagline-sm"
            >
              {{ blok.tagline }}
            </div>
            <div
              v-if="blok.headline"
              class="type-headline-3xl"
            >
              {{ blok.headline }}
            </div>
            <div class="wysiwyg max-w-[40ch]">
              <ExtendedWysiwyg :content="blok.content" />
            </div>
            <div v-if="blok.buttonText" class="flex flex-wrap gap-20 desk:gap-24 mt-12">
              <nuxt-link
                v-if="blok.buttonText"
                :to="$sbLinkFix(blok.link)"
                :target="$sbLinkTarget(blok.link)"
              >
                <div
                  class="btn"
                  :class="{
                    'btn--secondary' : blok.buttonStyle === 'secondary',
                    'btn--underline' : blok.buttonStyle === 'underline',
                    'btn--ghost' : blok.buttonStyle === 'ghost',
                  }"
                >
                  {{ blok.buttonText }}
                </div>
              </nuxt-link>
              <nuxt-link
                v-if="blok.secondaryButtonText"
                :to="$sbLinkFix(blok.secondaryLink)"
                :target="$sbLinkTarget(blok.secondaryLink)"
              >
                <div
                  class="btn"
                  :class="{
                    'btn--secondary' : blok.secondaryButtonStyle === 'secondary',
                    'btn--underline' : blok.secondaryButtonStyle === 'underline',
                    'btn--ghost' : blok.secondaryButtonStyle === 'ghost',
                  }"
                >
                  {{ blok.secondaryButtonText }}
                </div>
              </nuxt-link>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  StoryBlokPropBase,
  StoryBlokAsset,
  StoryBlokVisualApperance, StoryBlokLink, DatasourceObjectPosition
} from '~/constants/types/storyblok';

import { checkIfVideoFile } from '~/util/imageUtil';
import { AspectRatioKeys } from '~/constants/aspectRatioKeys';
import useVisualAppearancePlugin from '~/composeables/useVisualAppearancePlugin';
import { ISbRichtext } from 'storyblok-js-client';
import { useElementBounding } from '@vueuse/core';
import { computed, ref } from 'vue';

type Props = StoryBlokPropBase & {
  additionalImage: StoryBlokAsset;
  enableAdditionalImage: boolean;
  thirdImage: StoryBlokAsset;
  tagline: string;
  headline: string;
  content: ISbRichtext;
  buttonText: string;
  buttonStyle: 'primary' | 'secondary' | 'ghost' | 'underline';
  imageAspectRatio: AspectRatioKeys;
  imageOrBackgroundVideo: StoryBlokAsset;
  leftAlignedContent: boolean;
  textAlign?: 'left' | 'center' | 'right';
  visualAppearance: StoryBlokVisualApperance;
  link: StoryBlokLink;
  design: 'default' | 'imageOverlay' | 'colorBackground' | 'imageGrid' | 'imagesGrid';
  secondaryButtonText: string;
  secondaryButtonStyle: 'primary' | 'secondary' | 'ghost' | 'underline';
  secondaryLink: StoryBlokLink;
  backgroundColor: 'none' | 'subtle' | 'secondary' | 'accenture';
  focalPoint: DatasourceObjectPosition;
  secondFocalPoint: DatasourceObjectPosition;
  thirdFocalPoint: DatasourceObjectPosition;
  parallaxEffect: boolean;
}

const props = defineProps<{
  blok: Props,
}>();

const { desktopWidth, blockPaddingTop, blockPaddingBottom } = useVisualAppearancePlugin(props);

const route = useRoute();
const hideVideo = ref(false);
const videoKey = ref(1);
const isInStoryblokAdmin = (route.query && route.query._storyblok && route.query._storyblok !== '');

/**
 * This fixes a thing in storyblok admin where videos refused to change size
 */
watch(() => props.blok.imageAspectRatio, ()=> {
  if (isInStoryblokAdmin) {
    hideVideo.value = true;
    nextTick(()=> {
      videoKey.value++;
      setTimeout(()=> {
        hideVideo.value = false;
      }, 250);
    });
  }
});

const parallaxContainerRef = useTemplateRef('parallaxContainerRef');
const { top } = useElementBounding(parallaxContainerRef);

const windowHeight = ref(0);
onMounted(()=> {
  windowHeight.value = window.innerHeight;
});

const progress = computed(() => {
  const start = windowHeight.value;
  const end = -400;
  return Math.max(0, Math.min((start - top.value) / (start - end), 1));
});

const offset = computed(() => progress.value * 40);
const offsetLarge = computed(() => progress.value * 80);
const reverseOffset = computed(() => -offset.value);

</script>
<style scoped lang="postcss">

:deep(.wysiwyg p) {
  @apply text-16 desk:text-18 font-karla normal-case text-pretty text-text-subtle leading-24 desk:leading-26 desk:mt-12;
}
</style>
